import * as H from "history";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { debounce } from "throttle-debounce";
import { IRootState } from "../../../../store";
import DispatchProps from "../../../../utils/DispatchProps";
import ApiService from "../../../ApiService";
import MailroomSchoolsTable, {MailroomSchoolsTableRow} from "../../../mailroom/MailroomSchoolsTable";
import PageTitle from "../../../PageTitle";
import { Link } from "react-router-dom";
import { CommandBarButton, TextField } from "office-ui-fabric-react";
import { DurableFunctionInstance } from "../../../../models/DurableFunctionInstance";
import {MailroomSchoolConfig} from "../../../../models/MailroomSchoolConfig";
import * as _ from "lodash";

interface MailroomDashboardPageProps {
    apiService: ApiService;
    history: H.History;
}

interface MailroomDashboardPageState {
    mailroomSchoolConfigs?: MailroomSchoolConfig[];
    mailroomSchoolAutomations?: DurableFunctionInstance[] | undefined | null;
    filterString?: string;
    filteredMailroomSchoolConfigs: MailroomSchoolConfig[] | undefined;

    latestInboxUploadDates?: { [Key: string]: string }
    latestSdsUploadDates?: { [Key: string]: any }
}

class MailRoomDashboardPage extends Component<MailroomDashboardPageProps & DispatchProps, MailroomDashboardPageState> {
    static displayName = MailRoomDashboardPage.name;

    private readonly debouncedFilterSchools: debounce<() => void>;

    constructor(props: MailroomDashboardPageProps & DispatchProps) {
        super(props);

        this.debouncedFilterSchools = debounce(200, this.filterSchools);
        
        this.state = {
            filterString: undefined,
            filteredMailroomSchoolConfigs: undefined
        };
    }

    async componentDidMount() {
        const mailroomSchoolConfigs = await this.props.apiService.getSchoolsWithMailroomConfig();
        this.setState({ mailroomSchoolConfigs: mailroomSchoolConfigs});
        
        this.props.apiService.getAutomations().then(automations => {
            this.setState({ mailroomSchoolAutomations: automations });
        });
        
        this.props.apiService.getLatestSdsUploadDates().then(latestSdsUploadDates => {
            this.setState({latestSdsUploadDates})
        });

        this.props.apiService.getLatestInboxUploadDates().then(latestInboxUploadDates => {
            this.setState({latestInboxUploadDates})
        });
    }

    componentWillUnmount() {
        if (this.debouncedFilterSchools) {
            this.debouncedFilterSchools.cancel();
        }
    }

    async componentDidUpdate(prevProps: Readonly<MailroomDashboardPageProps & DispatchProps>, prevState: MailroomDashboardPageState): Promise<void> {

        if (this.state.mailroomSchoolConfigs !== prevState.mailroomSchoolConfigs) {
            this.debouncedFilterSchools();
        }

        if (this.state.filterString !== prevState.filterString) {
            this.debouncedFilterSchools();
        }
    }


    private filterSchools(): void {
        if (!this.state.mailroomSchoolConfigs) {
            this.setState({
                filteredMailroomSchoolConfigs: undefined
            });

            return;
        }

        if (this.state.filterString === undefined || this.state.filterString === "") {
            this.setState({
                filteredMailroomSchoolConfigs: this.state.mailroomSchoolConfigs!
            });

            return;
        }

        const filtered = this.state.mailroomSchoolConfigs!.filter((mc) => {
            if (
                (mc.school!.tenantId && mc.school!.tenantId.toLowerCase().indexOf(this.state.filterString!) !== -1) ||
                (mc.school!.tenantName && mc.school!.tenantName.toLowerCase().indexOf(this.state.filterString!) !== -1) ||
                (mc.school!.schoolCode && mc.school!.schoolCode.toLowerCase().indexOf(this.state.filterString!) !== -1) ||
                (mc.school!.schoolName && mc.school!.schoolName.toLowerCase().indexOf(this.state.filterString!) !== -1) ||
                (mc.school!.licenseReference && mc.school!.licenseReference.toLowerCase().indexOf(this.state.filterString!) !== -1)) {
                return true;
            }
            else {
                return false;
            }
        });

        this.setState({ filteredMailroomSchoolConfigs: filtered });
    }
    
    
    private buildTableItems(): MailroomSchoolsTableRow[] | undefined
    {
        if(this.state.filteredMailroomSchoolConfigs === undefined)
        {
            return undefined;
        }
        
        return this.state.filteredMailroomSchoolConfigs.map(config => {
            
            const dateKey = `${config.school?.tenantId}-${config.school?.schoolCode}`;

            let automationLoopInstance = undefined;
            if(this.state.mailroomSchoolAutomations !== undefined)
            {
                automationLoopInstance = this.state.mailroomSchoolAutomations?.find(x =>
                    _.includes(x?.durableFunctionInstanceId,config.school?.tenantId)
                    && x?.durableFunctionInstanceId?.endsWith(config.sdsProfileId!)) || null;
            }

            return {
                school: config.school,
                lastSdsUpload: this.state.latestSdsUploadDates === undefined ? "Loading" : this.state.latestSdsUploadDates[dateKey]?.createdAt,
                lastDataUpload: this.state.latestInboxUploadDates === undefined ? "Loading" : this.state.latestInboxUploadDates[dateKey],
                misDataServiceType: config.misDataServiceType,
                academicYear: config.academicYear,
                automationLoopInstance: automationLoopInstance
            };
        })
        
    }

    render() {
        return (
            <Fragment>
                <PageTitle title={"Schools"} />

                <div className="filter-controls p-top-1rem">
                    <div className="filter-controls__row">
                        <TextField className="filter-controls__input" autoFocus label="School Name/Code, Tenant Name/Id, License Ref" placeholder="Filter" 
                           onKeyUp={(keyEvent) => {
                               if(keyEvent.key === "Enter")
                               {
                                   if(this.state.filteredMailroomSchoolConfigs?.length === 1)
                                   {
                                       const filteredConfig = this.state.filteredMailroomSchoolConfigs[0];
                                       this.props.history.push(`/cdb-admin/mailroom/${filteredConfig.school?.tenantId}/${filteredConfig.school?.schoolCode}`);
                                   }
                               }
                           }}
                           onChange={
                            (event, newValue) => {
                                this.setState({
                                    filterString: newValue?.toLowerCase()
                                });
                            }
                        } />
                    </div>
                </div>


                <MailroomSchoolsTable
                    title="Mailroom"
                    items={this.buildTableItems()}
                    virtualize={false}>

                    <Link to={`/cdb-admin/mailroom/analytics`}>
                        <CommandBarButton
                            iconProps={{ iconName: "Chart" }}
                            text="Mailroom Analytics"
                            onClick={() => { }}
                        />
                    </Link>

                </MailroomSchoolsTable>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { history: H.History }): MailroomDashboardPageProps => {
        return {
            apiService: state.auth.apiService,
            history: state.history
        };
    }
)(MailRoomDashboardPage);